// GiteaPanel.tsx — the recent commits on a repository's default branch,
// straight from gitea, with docdaemon's own marked.
//
// This is the panel that answers the question the rest of the repository page
// cannot: a run says it committed sha abc1234, and this says whether that
// commit is actually on the branch and what landed around it. The daemon
// commits as its own author (docdaemon <docdaemon@lab0.us>), so "did my
// documentation land, and did it land on top of somebody's work" is one glance
// down the marked column.
//
// It fetches for itself rather than taking commits as a prop: gitea is a
// second service and can be down or slow while docdaemon's own API is fine, so
// this card fails alone instead of failing the page.
import { Fragment, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { StatusBadge } from 'common';
import { ExpandoChevron } from 'common/ui/Expando';
import { fetchCommits } from '../api';
import type { GitCommit } from '../api';
import CommitDetail from './CommitDetail';
import { absoluteTime, commitSubject, relativeTime, shortSha } from '../format';

const COMMIT_LIMIT = 20;

export default function GiteaPanel({ owner, name }: { owner: string; name: string }) {
  const [commits, setCommits] = useState<GitCommit[] | null>(null);
  const [error, setError] = useState('');
  // One open at a time. Several diffs expanded at once turns the panel into a
  // scroll of unrelated code, and the question being asked here is always
  // about one commit.
  const [openSha, setOpenSha] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const list = await fetchCommits(owner, name, COMMIT_LIMIT);
        if (live) setCommits(list);
      } catch (err) {
        if (live) setError(err instanceof Error ? err.message : 'Failed to load commits');
      }
    })();
    return () => { live = false; };
  }, [owner, name]);

  const authored = (commits ?? []).filter((c) => c.by_docdaemon).length;

  return (
    <Card className="mb-4">
      <Card.Header>Recent Commits</Card.Header>
      <Card.Body>
        {error ? (
          <p className="text-danger small m-0">Couldn’t reach gitea: {error}</p>
        ) : !commits ? (
          <div className="d-flex justify-content-center py-3"><Spinner animation="border" size="sm" /></div>
        ) : commits.length === 0 ? (
          <p className="text-muted small m-0">No commits on this branch yet.</p>
        ) : (
          <Table size="sm" hover responsive className="m-0">
            <thead>
              <tr>
                <th>Commit</th>
                <th>Subject</th>
                <th>Author</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {commits.map((commit) => (
                <Fragment key={commit.sha}>
                <tr>
                  <td className="align-middle">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 me-2 text-muted"
                      onClick={() => setOpenSha(openSha === commit.sha ? null : commit.sha)}
                      aria-expanded={openSha === commit.sha}
                      aria-label={openSha === commit.sha ? 'Hide the diff' : 'Show the diff'}
                    >
                      <ExpandoChevron open={openSha === commit.sha} />
                    </Button>
                    {/* Out to gitea, which owns the diff. rel=noreferrer as
                        well as noopener: the target is another origin. */}
                    <a href={commit.html_url} target="_blank" rel="noopener noreferrer">
                      <code>{shortSha(commit.sha)}</code>
                    </a>
                  </td>
                  <td className="align-middle">
                    {commitSubject(commit.message)}
                    {/* The mark is on the row that is ours, not a column of
                        "no" beside everyone else's work. */}
                    {commit.by_docdaemon && (
                      <StatusBadge tone="info" className="ms-2" uppercase={false}>docdaemon</StatusBadge>
                    )}
                  </td>
                  <td className="align-middle small text-muted" title={commit.author_email}>
                    {commit.author_name}
                  </td>
                  <td className="align-middle small text-muted" title={absoluteTime(commit.committed_at)}>
                    {relativeTime(commit.committed_at)}
                  </td>
                </tr>
                {openSha === commit.sha && (
                  <tr>
                    {/* The detail spans the table rather than sitting in one
                        column: a diff has nothing to do with the column
                        widths above it. */}
                    <td colSpan={4} className="bg-transparent">
                      <CommitDetail owner={owner} name={name} sha={commit.sha} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
      <Card.Footer className="small text-muted">
        {commits
          ? `${authored} of the last ${commits.length} commit${commits.length === 1 ? '' : 's'} written by docdaemon.`
          : `The ${COMMIT_LIMIT} most recent commits on the default branch.`}
      </Card.Footer>
    </Card>
  );
}
